import { graphql } from 'react-relay';
import type { GraphQLTaggedNode } from 'react-relay';

import { useEditorContentFragment } from '@confluence/editor-content-unified-query/entry-points/useEditorContentFragment.ts';
import type { EditorContentFragmentResult } from '@confluence/editor-content-unified-query/entry-points/useEditorContentFragment.ts';

import type { DevDocsOverwriteWarningBannerProps } from '@confluence/dev-docs/entry-points/DevDocsOverwriteWarningBanner.ts';

import type { useDevDocsAncestorsFragment_content$key } from './__generated__/useDevDocsAncestorsFragment_content.graphql.ts';

// The ContentHeaderUnifiedQuery ancestors the Dev Docs banner reads, for EditorContentUnifiedQuery.
const devDocsAncestorsFragmentNode: GraphQLTaggedNode = graphql`
	fragment useDevDocsAncestorsFragment_content on Content @catch {
		id
		ancestors {
			id
			title
			type
			subType
			history {
				createdBy {
					displayName
				}
			}
			properties(keys: ["devdocsProperties", "smartFolderProperties"]) {
				nodes {
					id
					key
					value
				}
			}
		}
	}
`;

// The ContentHeaderUnifiedQuery data the banner reads.
export type AncestorsQueryData = {
	content?: {
		nodes?: ReadonlyArray<{
			ancestors?: DevDocsOverwriteWarningBannerProps['ancestors'];
		} | null> | null;
	} | null;
};

/**
 * The Dev Docs banner ancestors from EditorContentUnifiedQuery in the shape of
 * ContentHeaderUnifiedQuery data. When the fragment is provided, the banner skips
 * ContentHeaderUnifiedQuery.
 */
export function useDevDocsAncestorsFragment({
	contentId,
}: {
	contentId: string;
}): EditorContentFragmentResult<AncestorsQueryData> {
	return useEditorContentFragment<useDevDocsAncestorsFragment_content$key, AncestorsQueryData>(
		'devDocsAncestorsFragment',
		devDocsAncestorsFragmentNode,
		{
			contentId,
			mapData: (value) => ({ content: { nodes: [value] } }),
		},
	);
}
